import React from 'react';
import { Navigate } from 'react-router-dom';
import { useConsentContext } from '@/contexts/ConsentContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPurposes: string[];
  fallbackPath?: string;
  showLoadingState?: boolean;
}

/**
 * ProtectedRoute - Clean Route Protection Without Modal Management
 * 
 * This component protects routes that require consent without managing
 * consent modals. The GlobalConsentManager handles all modal logic.
 * 
 * Key Features:
 * - Checks consent status for required purposes
 * - Redirects or shows blocking UI if consent is missing
 * - No modal management (handled by GlobalConsentManager)
 * - Clean separation of concerns
 */
export function ProtectedRoute({ 
  children, 
  requiredPurposes, 
  fallbackPath = '/patient/help',
  showLoadingState = true 
}: ProtectedRouteProps) {
  const {
    consentStatus,
    isLoading,
    checkPurposes
  } = useConsentContext();

  const [purposeCheck, setPurposeCheck] = React.useState<{
    hasValidConsent: boolean;
    missingPurposes: string[];
    isChecked: boolean;
  }>({
    hasValidConsent: false,
    missingPurposes: [],
    isChecked: false
  });

  console.log('🔍 PROTECTED_ROUTE:', {
    route: window.location.pathname,
    requiredPurposes,
    isLoading,
    purposeCheck,
    timestamp: new Date().toISOString()
  });

  // Check consent status for required purposes
  React.useEffect(() => {
    const checkRequiredPurposes = async () => {
      if (isLoading) return;

      try {
        const result = await checkPurposes(requiredPurposes);
        setPurposeCheck({
          hasValidConsent: result.hasValidConsent,
          missingPurposes: result.missingPurposes,
          isChecked: true
        });

        console.log('🔍 PROTECTED_ROUTE_CHECK:', {
          route: window.location.pathname,
          result,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('🔍 PROTECTED_ROUTE_ERROR:', {
          route: window.location.pathname,
          error: error.message,
          timestamp: new Date().toISOString()
        });
        setPurposeCheck({
          hasValidConsent: false,
          missingPurposes: requiredPurposes,
          isChecked: true
        });
      }
    };

    checkRequiredPurposes();
  }, [checkPurposes, requiredPurposes, isLoading, consentStatus]);

  // Show loading state while checking consent
  if (isLoading || !purposeCheck.isChecked) {
    if (showLoadingState) {
      return (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 animate-pulse text-blue-600" />
            <span className="text-sm text-gray-600">Verifying permissions...</span>
          </div>
        </div>
      );
    }
    return null;
  }

  // If consent is valid, render the protected content
  if (purposeCheck.hasValidConsent) {
    console.log('🔍 PROTECTED_ROUTE_ALLOW:', {
      route: window.location.pathname,
      timestamp: new Date().toISOString()
    });
    return <>{children}</>;
  }

  // If consent is missing, allow access - GlobalConsentManager will handle consent modal
  // This ensures users can access the route and see the consent modal from GlobalConsentManager
  if (!purposeCheck.hasValidConsent) {
    console.log('🔍 PROTECTED_ROUTE_ALLOW_PENDING:', {
      route: window.location.pathname,
      reason: 'consent_missing_global_manager_will_handle',
      missingPurposes: purposeCheck.missingPurposes,
      timestamp: new Date().toISOString()
    });
  }

  // Always allow access - GlobalConsentManager handles consent flow
  return <>{children}</>;
}
